/* ============================================================
   MEDIA LIBRARY — upload / list / replace / delete real images on
   Storage, with a Firestore media/{id} doc per asset:
     { name, url, storagePath, alt, width, height, type, createdAt }
   Images are optimized client-side (imageTools) before upload.
   The public site reads media via the same media/{id} docs.
   ============================================================ */
import { useEffect, useState } from 'react'
import { collection, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { db } from '../lib/firestore.js'
import { storage } from '../lib/storage.js'
import { optimizeImage, deriveName } from './imageTools.js'

export function useMediaLibrary() {
  const [media, setMedia] = useState([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    return onSnapshot(collection(db, 'media'), (snap) => {
      setMedia(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    })
  }, [])

  // Upload one image File. Optimizes, stores in Storage, writes media/{id}.
  async function upload(file) {
    setBusy(true)
    try {
      const { blob, type, width, height, ext } = await optimizeImage(file)
      const id = 'm_' + Math.random().toString(36).slice(2, 10)
      const storagePath = `media/${id}.${ext}`
      const sref = ref(storage, storagePath)
      await uploadBytes(sref, blob, { contentType: type })
      const url = await getDownloadURL(sref)
      await setDoc(doc(db, 'media', id), {
        name: deriveName(file.name),
        url,
        storagePath,
        alt: '',
        width,
        height,
        type,
        createdAt: serverTimestamp(),
      })
      return id
    } finally {
      setBusy(false)
    }
  }

  const setAlt = (id, alt) => setDoc(doc(db, 'media', id), { alt }, { merge: true })

  // Remove a media entry and its Storage object (best-effort on the blob).
  async function remove(item) {
    if (item.storagePath) {
      try {
        await deleteObject(ref(storage, item.storagePath))
      } catch (e) {
        console.warn('[media] blob delete failed (continuing).', e)
      }
    }
    await deleteDoc(doc(db, 'media', item.id))
  }

  return { media, busy, upload, setAlt, remove }
}
